import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, PaperPlaneTilt, FileText, PenNib, UserPlus, Trash, Eye } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TIP_CATEGORY_LABELS } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import SignaturePad from "../components/SignaturePad";

const STAGE_STYLE = {
  invited: "bg-muted text-muted-foreground", offer_sent: "bg-[#F5A623]/15 text-[#F5A623]",
  offer_signed: "bg-[#9D5CFF]/15 text-[#C9A8FF]", info_submitted: "bg-[#9D5CFF]/15 text-[#C9A8FF]",
  contract_generated: "bg-[#F5A623]/15 text-[#F5A623]", contract_signed: "bg-[#A9C97E]/15 text-[#A9C97E]",
  active: "bg-[#A9C97E]/15 text-[#A9C97E]", info_pending: "bg-[#F5A623]/15 text-[#F5A623]",
  done: "bg-[#A9C97E]/15 text-[#A9C97E]",
};
const NEW_CAND = { first_name: "", last_name: "", email: "", position: "", start_date: "", end_date: "",
                   probation_months: 3, salary_type: "hourly", rate: "", reporting_manager: "", working_hours: "" };
const NEW_EMP = { position: "", department: "", salary_type: "hourly", rate: "", tip_category: "",
                  reporting_manager: "", working_hours: "", working_hours_detail: "" };

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cands, setCands] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newC, setNewC] = useState(NEW_CAND);
  const [offerFor, setOfferFor] = useState(null); // candidate
  const [offerCfg, setOfferCfg] = useState({ template_id: "", signee_name: "", signee_title: "Owner" });
  const [empFor, setEmpFor] = useState(null);
  const [empForm, setEmpForm] = useState(NEW_EMP);
  const [contractFor, setContractFor] = useState(null);
  const [contractTpl, setContractTpl] = useState("");
  const [signFor, setSignFor] = useState(null);
  const [signName, setSignName] = useState("");
  const [sig, setSig] = useState(null);
  const [actFor, setActFor] = useState(null);
  const [actForm, setActForm] = useState({ pin: "", password: "" });
  const [revealFor, setRevealFor] = useState(null); // {cand, data}
  const [busy, setBusy] = useState(false);

  const revealPii = async (c) => {
    try {
      const { data } = await api.post(`/candidates/${c.id}/reveal`);
      setRevealFor({ cand: c, data });
      toast.info("Sensitive fields revealed — this view was logged in the audit trail");
    } catch (e) {
      toast.error(apiError(e, "Reveal failed"));
    }
  };

  const load = () => api.get("/candidates").then((r) => setCands(r.data)).catch(() => toast.error("Failed to load candidates"));
  useEffect(() => {
    load();
    api.get("/hr-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      setShowNew(false); setOfferFor(null); setEmpFor(null); setContractFor(null);
      setSignFor(null); setActFor(null); setSig(null); setSignName("");
      load();
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    } finally {
      setBusy(false);
    }
  };

  const offerTpls = templates.filter((t) => t.doc_type === "offer_letter");
  const contractTpls = templates.filter((t) => t.doc_type.startsWith("contract"));
  const fld = (state, setState, k, label, props = {}) => (
    <div className="space-y-1.5" key={k}>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={`ob-field-${k}`} value={state[k] ?? ""} onChange={(e) => setState({ ...state, [k]: e.target.value })} {...props} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold">Onboarding</h1>
          <p className="text-muted-foreground mt-1">Hiring pipeline — offer letters, contracts and e-signatures. Document wording is per venue in Settings → HR Documents.</p>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="ob-new-btn" className="bg-primary hover:bg-[#8B5CF6] text-white">
          <Plus size={16} className="mr-1" /> New candidate
        </Button>
      </div>

      <div className="glass border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="ob-table">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="px-4 py-3 font-medium">Candidate</th>
              <th className="px-4 py-3 font-medium">Position</th>
              <th className="px-4 py-3 font-medium">Start date</th>
              <th className="px-4 py-3 font-medium">Stage</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {(cands || []).map((c) => (
              <tr key={c.id} className="border-b last:border-0" data-testid={`ob-row-${c.id}`}>
                <td className="px-4 py-2.5">
                  <p className="font-medium">{c.first_name} {c.last_name}</p>
                  <p className="text-xs text-muted-foreground">{c.email}{c.kind === "existing" ? " · existing staff" : ""}</p>
                </td>
                <td className="px-4 py-2.5">{c.offer?.position || c.employment?.position || "—"}</td>
                <td className="px-4 py-2.5 num">{c.offer?.start_date || "—"}</td>
                <td className="px-4 py-2.5">
                  <Badge className={STAGE_STYLE[c.stage] || ""} data-testid={`ob-stage-${c.id}`}>{c.stage_label}</Badge>
                  {c.stage === "contract_generated" && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {c.contract?.candidate_sig ? `Candidate signed ${c.contract.candidate_sig.signed_at?.slice(0, 10)}` : "Awaiting candidate signature"}
                    </p>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1.5 justify-end flex-wrap">
                    {["invited", "offer_sent"].includes(c.stage) && (
                      <Button size="sm" variant="outline" className="text-xs h-7" data-testid={`ob-send-offer-${c.id}`}
                        onClick={() => { setOfferFor(c); setOfferCfg({ template_id: offerTpls[0]?.id || "", signee_name: `${user.first_name} ${user.last_name}`, signee_title: "Owner" }); }}>
                        <PaperPlaneTilt size={13} className="mr-1" /> {c.stage === "invited" ? "Send offer" : "Resend offer"}
                      </Button>
                    )}
                    {["offer_signed", "info_submitted"].includes(c.stage) && (
                      <Button size="sm" variant="outline" className="text-xs h-7" data-testid={`ob-employment-${c.id}`}
                        onClick={() => { setEmpFor(c); setEmpForm({ ...NEW_EMP, ...c.employment, position: c.employment?.position || c.offer?.position || "", rate: c.employment?.rate ?? c.offer?.rate ?? "" }); }}>
                        Employment details
                      </Button>
                    )}
                    {c.stage === "info_submitted" && (
                      <Button size="sm" variant="outline" className="text-xs h-7" data-testid={`ob-gen-contract-${c.id}`}
                        onClick={() => { setContractFor(c); setContractTpl(contractTpls[0]?.id || ""); }}>
                        <FileText size={13} className="mr-1" /> Generate contract
                      </Button>
                    )}
                    {c.stage === "contract_generated" && !c.contract?.employer_sig && (
                      <Button size="sm" variant="outline" className="text-xs h-7" data-testid={`ob-sign-employer-${c.id}`}
                        onClick={() => { setSignFor(c); setSignName(`${user.first_name} ${user.last_name}`); }}>
                        <PenNib size={13} className="mr-1" /> Sign as employer
                      </Button>
                    )}
                    {c.stage === "contract_signed" && (
                      <Button size="sm" className="text-xs h-7 bg-primary hover:bg-[#8B5CF6] text-white" data-testid={`ob-activate-${c.id}`}
                        onClick={() => { setActFor(c); setActForm({ pin: "", password: "" }); }}>
                        <UserPlus size={13} className="mr-1" /> Activate employee
                      </Button>
                    )}
                    {c.stage === "active" && c.employee_id && (
                      <Button size="sm" variant="ghost" className="text-xs h-7" data-testid={`ob-view-emp-${c.id}`}
                        onClick={() => navigate(`/employees/${c.employee_id}`)}>View employee</Button>
                    )}
                    {["info_submitted", "contract_generated", "contract_signed", "active"].includes(c.stage) && (
                      <Button size="sm" variant="ghost" className="text-xs h-7" data-testid={`ob-reveal-${c.id}`}
                        onClick={() => revealPii(c)}>
                        <Eye size={13} className="mr-1" /> PII
                      </Button>
                    )}
                    {user.role === "owner" && !["active", "done"].includes(c.stage) && (
                      <Button size="sm" variant="ghost" className="text-destructive h-7" data-testid={`ob-delete-${c.id}`}
                        onClick={() => run(() => api.delete(`/candidates/${c.id}`), "Candidate removed")}><Trash size={13} /></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {cands && cands.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground" data-testid="ob-empty">
                No candidates yet — add your next hire, or send an onboarding invite to existing staff from their profile.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="ob-new-dialog">
          <DialogHeader><DialogTitle className="font-heading">New candidate</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fld(newC, setNewC, "first_name", "Name", { required: true })}
            {fld(newC, setNewC, "last_name", "Surname", { required: true })}
            {fld(newC, setNewC, "email", "Email (offer letter goes here)", { type: "email", required: true })}
            {fld(newC, setNewC, "position", "Position")}
            {fld(newC, setNewC, "start_date", "Start date", { type: "date" })}
            {fld(newC, setNewC, "end_date", "End date (fixed-term only)", { type: "date" })}
            {fld(newC, setNewC, "probation_months", "Probation (months)", { type: "number", min: 0 })}
            <div className="space-y-1.5">
              <Label className="text-xs">Pay structure</Label>
              <Select value={newC.salary_type} onValueChange={(v) => setNewC({ ...newC, salary_type: v })}>
                <SelectTrigger data-testid="ob-field-salary-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="hourly">Hourly</SelectItem><SelectItem value="fixed">Fixed monthly</SelectItem></SelectContent>
              </Select>
            </div>
            {fld(newC, setNewC, "rate", newC.salary_type === "hourly" ? "Hourly rate (R)" : "Monthly salary (R)", { type: "number", step: "0.01" })}
            {fld(newC, setNewC, "reporting_manager", "Reports to")}
            {fld(newC, setNewC, "working_hours", "Working hours / schedule")}
          </div>
          <Button disabled={busy} data-testid="ob-create-submit" className="w-full bg-primary hover:bg-[#8B5CF6] text-white"
            onClick={() => run(async () => {
              await api.post("/candidates", { ...newC, rate: Number(newC.rate) || 0,
                probation_months: newC.probation_months === "" ? null : Number(newC.probation_months),
                start_date: newC.start_date || null, end_date: newC.end_date || null });
              setNewC(NEW_CAND);
            }, "Candidate created — send the offer letter when ready")}>
            {busy ? "Creating…" : "Create candidate"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!offerFor} onOpenChange={() => setOfferFor(null)}>
        <DialogContent className="max-w-md" data-testid="ob-offer-dialog">
          <DialogHeader><DialogTitle className="font-heading">Send offer letter — {offerFor?.first_name} {offerFor?.last_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Template</Label>
              <Select value={offerCfg.template_id} onValueChange={(v) => setOfferCfg({ ...offerCfg, template_id: v })}>
                <SelectTrigger data-testid="ob-offer-template"><SelectValue /></SelectTrigger>
                <SelectContent>{offerTpls.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} (v{t.version})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {fld(offerCfg, setOfferCfg, "signee_name", "Signee name (on the letter)")}
            {fld(offerCfg, setOfferCfg, "signee_title", "Signee title")}
            <Button disabled={busy || !offerCfg.template_id} data-testid="ob-offer-send" className="w-full bg-primary hover:bg-[#8B5CF6] text-white"
              onClick={() => run(() => api.post(`/candidates/${offerFor.id}/send-offer`, offerCfg), "Offer letter emailed to candidate")}>
              {busy ? "Sending…" : "Email offer letter"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!empFor} onOpenChange={() => setEmpFor(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="ob-employment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Employment details — {empFor?.first_name} {empFor?.last_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fld(empForm, setEmpForm, "position", "Position / job title")}
            {fld(empForm, setEmpForm, "department", "Department")}
            <div className="space-y-1.5">
              <Label className="text-xs">Pay structure</Label>
              <Select value={empForm.salary_type} onValueChange={(v) => setEmpForm({ ...empForm, salary_type: v })}>
                <SelectTrigger data-testid="ob-emp-salary-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="hourly">Hourly</SelectItem><SelectItem value="fixed">Fixed monthly</SelectItem></SelectContent>
              </Select>
            </div>
            {fld(empForm, setEmpForm, "rate", empForm.salary_type === "hourly" ? "Hourly rate (R)" : "Monthly salary (R)", { type: "number", step: "0.01" })}
            <div className="space-y-1.5">
              <Label className="text-xs">Tip category</Label>
              <Select value={empForm.tip_category || "none"} onValueChange={(v) => setEmpForm({ ...empForm, tip_category: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="ob-emp-tip-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {Object.entries(TIP_CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {fld(empForm, setEmpForm, "reporting_manager", "Reports to")}
            {fld(empForm, setEmpForm, "working_hours", "Working hours (summary)")}
            {fld(empForm, setEmpForm, "working_hours_detail", "Working hours detail (Annexure A)")}
          </div>
          <Button disabled={busy} data-testid="ob-employment-save" className="w-full bg-primary hover:bg-[#8B5CF6] text-white"
            onClick={() => run(() => api.put(`/candidates/${empFor.id}/employment`, { ...empForm, rate: Number(empForm.rate) || 0, tip_category: empForm.tip_category || null }), "Employment details saved")}>
            {busy ? "Saving…" : "Save employment details"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!contractFor} onOpenChange={() => setContractFor(null)}>
        <DialogContent className="max-w-md" data-testid="ob-contract-dialog">
          <DialogHeader><DialogTitle className="font-heading">Generate contract — {contractFor?.first_name} {contractFor?.last_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Contract template</Label>
              <Select value={contractTpl} onValueChange={setContractTpl}>
                <SelectTrigger data-testid="ob-contract-template"><SelectValue /></SelectTrigger>
                <SelectContent>{contractTpls.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} (v{t.version})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">Merges the candidate's personal info with your employment details, then emails the candidate a signing link.</p>
            <Button disabled={busy || !contractTpl} data-testid="ob-contract-generate" className="w-full bg-primary hover:bg-[#8B5CF6] text-white"
              onClick={() => run(() => api.post(`/candidates/${contractFor.id}/generate-contract`, { template_id: contractTpl }), "Contract generated and emailed to candidate")}>
              {busy ? "Generating…" : "Generate & email contract"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!signFor} onOpenChange={() => setSignFor(null)}>
        <DialogContent className="max-w-lg" data-testid="ob-sign-dialog">
          <DialogHeader><DialogTitle className="font-heading">Employer signature — {signFor?.first_name} {signFor?.last_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Your full name</Label>
              <Input value={signName} onChange={(e) => setSignName(e.target.value)} data-testid="ob-sign-name" />
            </div>
            <SignaturePad onChange={setSig} testid="ob-sig" />
            <Button disabled={busy || !signName.trim() || !sig} data-testid="ob-sign-submit" className="w-full bg-primary hover:bg-[#8B5CF6] text-white"
              onClick={() => run(() => api.post(`/candidates/${signFor.id}/employer-sign`, { name: signName.trim(), signature_data: sig }), "Contract countersigned")}>
              {busy ? "Signing…" : "Sign contract"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!revealFor} onOpenChange={() => setRevealFor(null)}>
        <DialogContent className="max-w-md" data-testid="ob-reveal-dialog">
          <DialogHeader><DialogTitle className="font-heading">Sensitive details — {revealFor?.cand.first_name} {revealFor?.cand.last_name}</DialogTitle></DialogHeader>
          <p className="text-[11px] text-muted-foreground">Decrypted on demand · this view was recorded in the audit log</p>
          {revealFor && (
            <dl className="grid grid-cols-2 gap-3 text-sm">
              {[["ID / passport", "id_number"], ["Phone", "phone"], ["Address", "address"], ["Bank", "bank_name"],
                ["Account number", "account_number"], ["Branch code", "branch_code"], ["Account type", "account_type"],
                ["Emergency contact", "emergency_contact_name"], ["Emergency phone", "emergency_contact_phone"]].map(([l, k]) => (
                <div key={k}><dt className="text-xs text-muted-foreground">{l}</dt>
                  <dd className="font-medium break-all" data-testid={`ob-reveal-${k}`}>{revealFor.data[k] || "—"}</dd></div>
              ))}
            </dl>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!actFor} onOpenChange={() => setActFor(null)}>
        <DialogContent className="max-w-md" data-testid="ob-activate-dialog">
          <DialogHeader><DialogTitle className="font-heading">Activate — {actFor?.first_name} {actFor?.last_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Creates the employee record, files the signed offer letter and contract to their Staff Directory profile, and starts leave/time tracking from their start date.</p>
            {fld(actForm, setActForm, "pin", "Clock-in PIN (4 digits)", { maxLength: 4 })}
            {fld(actForm, setActForm, "password", "Login password (optional — can be set later)", { type: "text" })}
            <Button disabled={busy || actForm.pin.length !== 4} data-testid="ob-activate-submit" className="w-full bg-primary hover:bg-[#8B5CF6] text-white"
              onClick={() => run(() => api.post(`/candidates/${actFor.id}/activate`, { ...actForm, password: actForm.password || null }), "Employee activated — documents filed")}>
              {busy ? "Activating…" : "Activate employee"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
